/**
 * Обработка микрофона в голосе: RNNoise (WebAssembly) и ворота по голосу или push-to-talk.
 * Реализует TrackProcessor LiveKit: дорожка микрофона проходит через AudioWorklet
 * (micWorklet.js), а в SFU уходит обработанная. Байты WebAssembly загружаются здесь и
 * передаются в worklet — там нет fetch; компилирует их сам worklet.
 */
import type { AudioProcessorOptions, TrackProcessor } from 'livekit-client'
import { Track } from 'livekit-client'
import rnnoiseWasmUrl from '@jitsi/rnnoise-wasm/dist/rnnoise.wasm?url'

export type InputMode = 'voice' | 'ptt'

export interface MicConfig {
  mode: InputMode
  /** Автоматический порог по уровню шума (и VAD RNNoise); иначе threshold в дБ. */
  auto: boolean
  threshold: number
  rnnoise: boolean
  /** Общая громкость микрофона (1 — как есть, до 2). */
  volume: number
}

export interface MicLevel {
  db: number
  open: boolean
  vad: number
}

let wasmBytes: Promise<ArrayBuffer> | null = null

/** Байты, а не готовый модуль: структурное копирование WebAssembly.Module в аудиопоток
 *  Chrome выполняет с задержкой в секунды, и всё это время шумоподавления просто нет. */
function loadWasm(): Promise<ArrayBuffer> {
  wasmBytes ??= fetch(rnnoiseWasmUrl).then((r) => {
    if (!r.ok) throw new Error(`RNNoise: ${r.status}`)
    return r.arrayBuffer()
  })
  return wasmBytes
}

export class MicProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  name = 'klichat-mic'
  processedTrack?: MediaStreamTrack
  onLevel: ((level: MicLevel) => void) | null = null
  onGate: ((open: boolean) => void) | null = null
  onError: ((message: string) => void) | null = null
  /** RNNoise действительно запустился в worklet'е (или отвалился). */
  onRnnoise: ((on: boolean) => void) | null = null

  private config: MicConfig
  private ctx: AudioContext | null = null
  private ownContext = false
  private source: MediaStreamAudioSourceNode | null = null
  private node: AudioWorkletNode | null = null
  private dest: MediaStreamAudioDestinationNode | null = null
  private ptt = false

  constructor(config: MicConfig) {
    this.config = config
  }

  async init(opts: AudioProcessorOptions): Promise<void> {
    if (opts.kind !== Track.Kind.Audio) throw new Error('klichat-mic: только аудио')
    await this.destroy()
    let ctx = opts.audioContext
    if (!ctx) {
      ctx = new AudioContext({ sampleRate: 48000 })
      this.ownContext = true
    }
    this.ctx = ctx
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
    await ctx.audioWorklet.addModule(new URL('./micWorklet.js', import.meta.url))
    this.source = ctx.createMediaStreamSource(new MediaStream([opts.track]))
    // байты кладём в processorOptions: worklet скомпилирует их в своём конструкторе,
    // до подключения к графу, — шумоподавление работает с первого кадра
    const wasm = this.config.rnnoise ? await loadWasm().catch((err: unknown) => this.failWasm(err)) : undefined
    this.node = new AudioWorkletNode(ctx, 'klichat-mic', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: { ...this.config, ...(wasm ? { wasm } : {}) },
    })
    this.node.port.onmessage = (e: MessageEvent<{ type: string; open?: boolean; db?: number; vad?: number; message?: string; on?: boolean }>) => {
      const m = e.data
      if (m.type === 'gate') this.onGate?.(Boolean(m.open))
      else if (m.type === 'level') this.onLevel?.({ db: m.db ?? -100, open: Boolean(m.open), vad: m.vad ?? -1 })
      else if (m.type === 'rnnoise') this.onRnnoise?.(Boolean(m.on))
      else if (m.type === 'error') this.onError?.(m.message ?? 'RNNoise')
    }
    this.dest = ctx.createMediaStreamDestination()
    this.source.connect(this.node)
    this.node.connect(this.dest)
    const track = this.dest.stream.getAudioTracks()[0]
    if (!track) throw new Error('klichat-mic: нет выходной дорожки')
    this.processedTrack = track
    this.node.port.postMessage({ type: 'ptt', down: this.ptt })
  }

  async restart(opts: AudioProcessorOptions): Promise<void> {
    // LiveKit при перезахвате дорожки свой AudioContext не передаёт — держимся за прежний,
    // иначе обработка уедет во второй контекст, мимо приглушения и вычитания голосов.
    // Читаем до init: он начинается с destroy, а тот контекст обнуляет.
    const ctx = this.ctx
    await this.init({ ...opts, ...(opts.audioContext ? {} : ctx ? { audioContext: ctx } : {}) })
  }

  async destroy(): Promise<void> {
    this.source?.disconnect()
    this.node?.disconnect()
    this.node?.port.close()
    this.processedTrack?.stop()
    this.source = null
    this.node = null
    this.dest = null
    delete this.processedTrack
    if (this.ownContext && this.ctx) await this.ctx.close().catch(() => undefined)
    this.ctx = null
    this.ownContext = false
  }

  configure(patch: Partial<MicConfig>): void {
    const wantWasm = patch.rnnoise && !this.config.rnnoise
    this.config = { ...this.config, ...patch }
    this.node?.port.postMessage({ type: 'config', ...patch })
    if (wantWasm) this.sendWasm()
  }

  /** Push-to-talk: клавиша нажата или отпущена. */
  setPTT(down: boolean): void {
    if (this.ptt === down) return
    this.ptt = down
    this.node?.port.postMessage({ type: 'ptt', down })
  }

  /** RNNoise включили на ходу: байты уезжают отдельным сообщением. */
  private sendWasm(): void {
    const node = this.node
    if (!node) return
    loadWasm()
      .then((bytes) => {
        if (this.node === node) node.port.postMessage({ type: 'wasm', bytes })
      })
      .catch((err: unknown) => this.failWasm(err))
  }

  private failWasm(err: unknown): undefined {
    this.onRnnoise?.(false)
    this.onError?.(err instanceof Error ? err.message : 'RNNoise недоступен')
    return undefined
  }
}
